import { BarChart, MapChart, PieChart } from "echarts/charts";
import {
  GeoComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { useEffect, useRef } from "react";

// Tree-shaken registration: pulling in only what is used keeps the bundle a
// fraction of the full echarts build.
echarts.use([
  BarChart,
  MapChart,
  PieChart,
  GeoComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
]);

export type EChartOption = Parameters<echarts.ECharts["setOption"]>[0];

interface Props {
  option: EChartOption;
  /** Must include the axis band, not just the plot area. */
  height: number;
  /** Description for assistive technology; the table view carries the values. */
  ariaLabel: string;
}

/**
 * Minimal ECharts wrapper.
 *
 * Deliberately not `echarts-for-react`: this is ~40 lines, avoids a dependency
 * with its own React peer-range, and keeps the instance lifecycle explicit.
 */
export function EChart({ option, height, ariaLabel }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const chart = echarts.init(el, undefined, { renderer: "canvas" });
    chartRef.current = chart;

    // Container-based rather than window-based: the sidebar collapsing changes
    // the chart's width without a window resize event.
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(el);

    return () => {
      observer.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    // notMerge: theme changes swap colors wholesale, and merging would leave
    // stale series styling behind.
    chartRef.current?.setOption(option, { notMerge: true });
  }, [option]);

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={ariaLabel}
      style={{ width: "100%", height }}
    />
  );
}
